import store_patient from '@/store/mods/clinique/patients'
import { telephone_affiche, titre_telephone } from '@/utils/telephone'
import { date_lisible } from '@/utils/rendez-vous'

// Le sélecteur est aussi utilisé isolément (tests, fenêtre autonome) : le mixin
// global n'est alors pas forcément monté. Le repli conserve le même rendu M/F.
const sexeCourt = (view, value, fallback = '') => {
    if (typeof view.formatSexeCourt === 'function') return view.formatSexeCourt(value, fallback)
    const source = value && typeof value === 'object' ? (value.specificatif || value.description || value.libelle || '') : value
    const raw = source == null ? '' : String(source).trim()
    const normalized = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalized)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalized)) return 'F'
    return raw || fallback
}

const texte = valeur => (valeur === null || valeur === undefined ? '' : String(valeur).trim())
const pagesEnCache = new Map()
const DUREE_CACHE = 30000

// CAISSE-VDF : choix du patient à la vente — tableau complet affiché directement (dossier, identité, téléphone, assurance,
// société, numéro de carte d'assurance), recherche côté Clinique (nom, dossier, téléphone, numéro de carte), pagination.
// « Choisir » ouvre la vente pour ce patient.
export default {
    name: 'choix-patient-vente',
    props: {
        toggler: { type: Number, default: 0 },
        disabled: { type: Boolean, default: false },
        afficherFiltres: { type: Boolean, default: true },
        actionLibelle: { type: String, default: 'Vendre à' }
    },
    data() {
        return { recherche: '', assurance_scope: 'all', page: 1, pages: 1, total: 0, patients: [], ouverts: [], is_loading: false, erreur: '', sequence: 0, detruit: false, search_timer: null, a_charge: false, filtre_affiche: '' }
    },

    watch: {
        toggler() {
            this.charger()
        }
    },

    mounted() {
        this.charger()
    },

    beforeDestroy() {
        this.detruit = true
        this.sequence++
        clearTimeout(this.search_timer)
    },

    methods: {
        charger() {
            if (this.detruit) return Promise.resolve()
            clearTimeout(this.search_timer)
            this.search_timer = null
            const sequence = ++this.sequence
            const filtre = { page: this.page, per_page: 25, assurance_scope: this.assurance_scope }
            const terme = this.recherche.trim()
            if (terme) filtre.q = terme
            const utilisateur = this.$store && this.$store.getters && this.$store.getters.authInfo
            const identifiant = utilisateur && utilisateur.id ? utilisateur.id : 'anonyme'
            const cle = JSON.stringify([identifiant, this.toggler, filtre.page, filtre.assurance_scope, terme])
            const entree = pagesEnCache.get(cle)
            if (!this.a_charge && entree && Date.now() - entree.date < DUREE_CACHE) {
                this.patients = entree.data
                this.total = entree.total
                this.pages = entree.pages
                this.filtre_affiche = cle
                this.a_charge = true
                this.is_loading = false
                this.erreur = ''
                return Promise.resolve()
            }
            this.a_charge = true
            if (this.filtre_affiche !== cle) {
                this.patients = []
                this.total = 0
                this.pages = 1
                this.ouverts = []
            }
            this.is_loading = true
            this.erreur = ''
            return store_patient.getPatientForSale(filtre).then((reponse) => {
                if (this.detruit || sequence !== this.sequence) return
                if (!reponse || !Array.isArray(reponse.data)) throw new Error('Réponse invalide')
                this.patients = reponse.data
                this.total = Number(reponse.total) || reponse.data.length
                this.pages = Math.max(1, Number(reponse.last_page) || 1)
                this.filtre_affiche = cle
                pagesEnCache.set(cle, { data: reponse.data, total: this.total, pages: this.pages, date: Date.now() })
                if (pagesEnCache.size > 40) pagesEnCache.delete(pagesEnCache.keys().next().value)
                this.ouverts = this.ouverts.filter(id => reponse.data.some(patient => patient.patient_id === id))
                this.is_loading = false
                if (!reponse.data.length && this.page > this.pages) {
                    this.page = this.pages
                    return this.charger()
                }
            }).catch(() => {
                if (this.detruit || sequence !== this.sequence) return
                this.is_loading = false
                this.erreur = 'Impossible de charger les patients. Réessayez.'
            })
        },

        chercher() {
            clearTimeout(this.search_timer)
            this.search_timer = setTimeout(() => { this.search_timer = null; this.page = 1; this.charger() }, 300)
        },

        rechercher_maintenant() {
            clearTimeout(this.search_timer)
            this.search_timer = null
            this.page = 1
            return this.charger()
        },

        filtrer_assurance(scope) {
            if (!['all', 'uninsured', 'insured'].includes(scope) || scope === this.assurance_scope) return Promise.resolve()
            this.assurance_scope = scope
            this.page = 1
            this.ouverts = []
            return this.charger()
        },

        aller_page(page) {
            if (this.is_loading || page < 1 || page > this.pages || page === this.page) return Promise.resolve()
            this.page = page
            return this.charger()
        },

        choisir(patient) {
            if (this.disabled || !patient || !patient.patient_id) return
            this.$emit('emitChoisirPatient', patient)
        },

        est_ouvert(id) {
            return this.ouverts.includes(id)
        },

        basculer_details(id) {
            this.ouverts = this.est_ouvert(id) ? this.ouverts.filter(item => item !== id) : this.ouverts.concat([id])
        },

        id_details(patient) {
            return 'vente-patient-details-' + this._uid + '-' + patient.patient_id
        },

        telephone(patient) {
            return telephone_affiche(patient, 'patient_telephone')
        },

        titre_telephone(patient) {
            return titre_telephone(patient, 'patient_telephone')
        },

        details(patient) {
            const naissance = texte(patient.date_naissance).substr(0, 10)
            const adresse = [texte(patient.adresse), texte(patient.quartier)].filter((valeur, index, liste) => valeur && liste.indexOf(valeur) === index).join(', ')
            const accompagnant = [texte(patient.accompagnant_nom) + (texte(patient.accompagnant_filiation) ? ' (' + texte(patient.accompagnant_filiation) + ')' : ''), texte(patient.accompagnant_telephone)].filter(Boolean).join(' · ')
            const a_prevenir = [texte(patient.personne_a_prevenir_nom), texte(patient.personne_a_prevenir_telephone)].filter(Boolean).join(' · ')
            return [
                ['N° dossier', texte(patient.patient_code)],
                ['Date de naissance', /^\d{4}-\d{2}-\d{2}$/.test(naissance) ? date_lisible(naissance) : ''],
                ['Adresse / quartier', adresse],
                ['Société souscriptrice', texte(patient.societe_souscripteur)],
                ['Accompagnant', accompagnant],
                ['Personne à prévenir', a_prevenir]
            ].map(detail => [detail[0], detail[1] || '—'])
        },

        sexe_age(patient) {
            const morceaux = []
            const sexe = sexeCourt(this, patient.sexe, '')
            if (sexe) morceaux.push(sexe)
            if (patient.age) morceaux.push(patient.age)
            return morceaux.length ? morceaux.join(' / ') : '—'
        }
    }
}
